/**
 * Sector-aware Balance Sheet ratio benchmark configuration.
 *
 * Thresholds are an initial heuristic screening policy, not peer-derived
 * facts and not investing recommendations.
 */

import {
    BENCHMARK_SOURCE_HEURISTIC,
    HEURISTIC_EFFECTIVE_FROM,
} from './sectorTaxonomy';

export const SCORED_METRIC_IDS = [
    'debtToEquity',
    'currentRatio',
    'quickRatio',
    'debtToAssets',
    'assetTurnoverRatio',
    'inventoryTurnoverRatio',
    'receivablesTurnoverRatio',
];

export const METRIC_LABELS = {
    debtToEquity: 'Debt to Equity Ratio (x)',
    currentRatio: 'Current Ratio (x)',
    quickRatio: 'Quick Ratio (Acid Test) (x)',
    debtToAssets: 'Debt to Assets Ratio (%)',
    assetTurnoverRatio: 'Asset Turnover Ratio (x)',
    inventoryTurnoverRatio: 'Inventory Turnover (x)',
    receivablesTurnoverRatio: 'Receivables Turnover (x)',
};

export const PERCENTAGE_METRIC_IDS = new Set(['debtToAssets']);

export const INVENTORY_NOT_APPLICABLE_ARCHETYPES = new Set(['assetLightServices']);

export const BALANCE_SHEET_NOT_APPLICABLE_REASONS = {
    financialInstitutions:
        'Bank/NBFC leverage, liquidity, and turnover ratios are not comparable to operating companies. Capital adequacy, loan-to-deposit, and similar measures are the correct tools.',
    reitInvit:
        'REIT and InvIT balance-sheet quality is judged on loan-to-value, occupancy, and distribution coverage rather than industrial debt/equity and working-capital ratios.',
};

export const INVENTORY_NOT_APPLICABLE_NOTE =
    'Inventory turnover is not applicable for asset-light and digital businesses that do not hold meaningful inventory.';

const HIGHER = 'higherIsBetter';
const LOWER = 'lowerIsBetter';
const BAND = 'band';

function heuristic(metricId, direction, thresholds) {
    return {
        source: BENCHMARK_SOURCE_HEURISTIC,
        effectiveFrom: HEURISTIC_EFFECTIVE_FROM,
        sampleSize: null,
        metricId,
        direction,
        thresholds,
        unit: PERCENTAGE_METRIC_IDS.has(metricId) ? 'percentage' : 'multiple',
    };
}

function higher(metricId, strong, watch) {
    return heuristic(metricId, HIGHER, { strong, watch });
}

function lower(metricId, strong, watch) {
    return heuristic(metricId, LOWER, { strong, watch });
}

function band(metricId, strongMin, strongMax, watchMin, watchMax) {
    return heuristic(metricId, BAND, { strongMin, strongMax, watchMin, watchMax });
}

function archetypeMetrics({
    debtToEquity,
    currentRatio,
    quickRatio,
    debtToAssets,
    assetTurnoverRatio,
    inventoryTurnoverRatio,
    receivablesTurnoverRatio,
}) {
    return {
        debtToEquity: band('debtToEquity', debtToEquity[0], debtToEquity[1], debtToEquity[2], debtToEquity[3]),
        currentRatio: band('currentRatio', currentRatio[0], currentRatio[1], currentRatio[2], currentRatio[3]),
        quickRatio: band('quickRatio', quickRatio[0], quickRatio[1], quickRatio[2], quickRatio[3]),
        debtToAssets: lower('debtToAssets', debtToAssets[0], debtToAssets[1]),
        assetTurnoverRatio: higher('assetTurnoverRatio', assetTurnoverRatio[0], assetTurnoverRatio[1]),
        inventoryTurnoverRatio: inventoryTurnoverRatio
            ? higher('inventoryTurnoverRatio', inventoryTurnoverRatio[0], inventoryTurnoverRatio[1])
            : null,
        receivablesTurnoverRatio: higher('receivablesTurnoverRatio', receivablesTurnoverRatio[0], receivablesTurnoverRatio[1]),
    };
}

/**
 * Initial heuristic thresholds by economic archetype.
 * Percentage values are percentage points (15 means 15%, not 0.15).
 */
export const ARCHETYPE_THRESHOLDS = {
    assetLightServices: archetypeMetrics({
        debtToEquity: [0, 0.25, 0, 0.50],
        currentRatio: [1.0, 2.2, 0.8, 3.0],
        quickRatio: [0.8, 1.8, 0.6, 2.5],
        debtToAssets: [15, 30],
        assetTurnoverRatio: [1.50, 0.90],
        inventoryTurnoverRatio: null,
        receivablesTurnoverRatio: [5, 3],
    }),
    defensiveConsumer: archetypeMetrics({
        debtToEquity: [0, 0.40, 0, 0.70],
        currentRatio: [1.2, 2.4, 0.9, 3.2],
        quickRatio: [0.9, 1.8, 0.7, 2.5],
        debtToAssets: [25, 40],
        assetTurnoverRatio: [1.20, 0.80],
        inventoryTurnoverRatio: [8, 5],
        receivablesTurnoverRatio: [8, 5],
    }),
    healthcareChemicals: archetypeMetrics({
        debtToEquity: [0, 0.45, 0, 0.80],
        currentRatio: [1.3, 2.5, 1.0, 3.5],
        quickRatio: [1.0, 2.0, 0.7, 2.8],
        debtToAssets: [25, 40],
        assetTurnoverRatio: [0.80, 0.50],
        inventoryTurnoverRatio: [5, 3],
        receivablesTurnoverRatio: [5, 3],
    }),
    manufacturingIndustrial: archetypeMetrics({
        debtToEquity: [0, 0.60, 0, 1.00],
        currentRatio: [1.2, 2.5, 1.0, 3.5],
        quickRatio: [0.8, 1.8, 0.6, 2.5],
        debtToAssets: [35, 50],
        assetTurnoverRatio: [0.80, 0.50],
        inventoryTurnoverRatio: [4, 2.5],
        receivablesTurnoverRatio: [5, 3],
    }),
    commodityCyclical: archetypeMetrics({
        debtToEquity: [0, 0.30, 0, 0.55],
        currentRatio: [1.5, 3.0, 1.2, 4.0],
        quickRatio: [1.0, 2.2, 0.8, 3.0],
        debtToAssets: [20, 35],
        assetTurnoverRatio: [0.55, 0.30],
        inventoryTurnoverRatio: [3, 1.5],
        receivablesTurnoverRatio: [6, 3.5],
    }),
    longCycleProject: archetypeMetrics({
        debtToEquity: [0, 1.20, 0, 2.00],
        currentRatio: [1.4, 2.8, 1.1, 4.0],
        quickRatio: [1.0, 2.0, 0.8, 3.0],
        debtToAssets: [45, 60],
        assetTurnoverRatio: [0.40, 0.20],
        inventoryTurnoverRatio: [2, 1.0],
        receivablesTurnoverRatio: [3, 1.5],
    }),
    regulatedNetworkCapex: archetypeMetrics({
        debtToEquity: [0, 1.50, 0, 2.50],
        currentRatio: [0.8, 1.8, 0.6, 2.5],
        quickRatio: [0.5, 1.4, 0.4, 2.0],
        debtToAssets: [55, 70],
        assetTurnoverRatio: [0.35, 0.18],
        inventoryTurnoverRatio: [4, 2],
        receivablesTurnoverRatio: [6, 4],
    }),
    media: archetypeMetrics({
        debtToEquity: [0, 0.50, 0, 0.90],
        currentRatio: [1.1, 2.4, 0.9, 3.2],
        quickRatio: [0.8, 1.8, 0.6, 2.5],
        debtToAssets: [30, 45],
        assetTurnoverRatio: [0.70, 0.40],
        inventoryTurnoverRatio: [6, 3],
        receivablesTurnoverRatio: [5, 3],
    }),
    notApplicable: null,
};
